import { useQuery } from '@tanstack/react-query';

import type { RequestError, SwaggerResponse } from '@/services/config';
import { Http } from '@/services/httpRequest';

import { mapClientVideoStreamSettings } from './mappers';
import type { ClientVideoStreamSettings, UseGetClientVideoStreamSettingsResult } from './types';

const getClientVideoStreamSettingsKey = (clientId: number) =>
    ['clientVideoStreamSettings', clientId] as const;

const emptyClientVideoStreamSettings: ClientVideoStreamSettings = {
    rows: [],
    locations: [],
};

export const useGetClientVideoStreamSettings = (
    clientId: number
): UseGetClientVideoStreamSettingsResult => {
    const query = useQuery<
        SwaggerResponse<unknown[]>,
        RequestError | Error,
        ClientVideoStreamSettings
    >({
        queryKey: getClientVideoStreamSettingsKey(clientId),
        queryFn: async (): Promise<SwaggerResponse<unknown[]>> =>
            Http.getRequest(
                `/api/v2/client/internal/${clientId}/video-stream-settings`,
                undefined,
                undefined,
                undefined
            ),
        // Axios: body is in `res.data` (the JSON array from Nest, not res.data.data).
        select: (axiosRes) => {
            const body = axiosRes.data as unknown;
            const rows = Array.isArray(body) ? body : [];
            return mapClientVideoStreamSettings(rows);
        },
    });

    return {
        data: query.data ?? emptyClientVideoStreamSettings,
        isLoading: query.isPending,
        isFetched: query.isFetched,
        isFetching: query.isFetching,
        isError: query.isError,
        error: query.error ?? null,
        refetch: () => {
            void query.refetch();
        },
    };
};

export { getClientVideoStreamSettingsKey };
